<template>
  <div class="h-full flex flex-col">
    <ChatHeader
      :title="dialogTitle"
      @update:title="updateTitle"
      @close="$emit('close')"
    />
    <div ref="messagesContainer" class="flex-1 flex flex-col gap-2 overflow-y-auto p-4">
      <div v-if="isLoading" class="flex items-center justify-center h-full">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-gpx_slate-100"></div>
      </div>
      <template v-else>
        <!-- Message grouping by date -->
        <template v-for="(group, date) in groupedMessages" :key="date">
          <!-- Date header for each new day -->
          <div v-if="showDateDivider(date)" class="text-center text-xs text-gpx_gray-700 my-3">
            {{ formatDate(date) }}
          </div>

          <template v-for="(messageGroup, groupIndex) in processMessagesIntoGroups(group)" :key="groupIndex">
            <MessageItem
              v-if="messageGroup.role === 'user'"
              :message="messageGroup"
              :avatar="userAvatar"
            />

            <ThinkingGroup
              v-else-if="messageGroup.role === 'thinking'"
              :messages="messageGroup.messages"
              :avatar="scoutIconSrc"
              :isComplete="!isScoutTyping"
            />

            <div v-else-if="messageGroup.role === 'action'"
                 class="relative flex mb-3 justify-start"
            >
              <div class="flex justify-end w-[90%] ml-2">
                <div class="flex flex-col gap-2 pr-0">
                  <ActionItem
                    v-for="action in messageGroup.messages"
                    :key="action.id"
                    :action="action"
                  />
                </div>
              </div>
            </div>

            <MessageItem
              v-else-if="messageGroup.role === 'answer'"
              :message="messageGroup"
              :avatar="scoutIconSrc"
              :isFinalAiMessage="true"
              @feedback-updated="handleFeedbackUpdated"
            />
          </template>
        </template>

        <div class="relative flex justify-start mb-1" v-if="isLastMessageFromUser && isScoutTyping">
          <div
            class="absolute -left-3 -top-3 w-7 overflow-hidden z-10"
            style="display: flex; justify-content: center; align-items: center; padding: 1px;"
          >
            <img :src="scoutIconSrc" alt="Scout" class="max-w-full max-h-full object-contain" />
          </div>
          <TypingIndicator class="ml-2" />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import {computed, ref, watch, nextTick, onMounted, onUnmounted} from 'vue';
import { getAiDialogMessages, getAccountDialogs, updateAiDialog} from '@gpx-spa/queries';

import { useTypingStore } from '@/stores/useTypingStore';
import { useDialogMessagesStore } from '@/stores/useDialogMessagesStore';
import { useStore } from 'vuex';
import ChatHeader from './ChatHeader.vue';
import TypingIndicator from './TypingIndicator.vue';
import MessageItem from './MessageItem.vue';
import ThinkingGroup from './ThinkingGroup.vue';
import scoutIconSrc from '@/assets/scout/red.svg';
import { format, isToday, isYesterday, fromUnixTime, startOfDay, getUnixTime } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { getCookie } from '@/utils/cookie';
import * as event from '@/events';
import ActionItem from "@/components/ai/ActionItem.vue";

const props = defineProps(['dialogId']);
const emit = defineEmits(['close']);

const dialogTitle = ref('Untitled Conversation');
const messagesContainer = ref(null);
const liveMessages = ref([]);
const isLoading = ref(false);

// Initialize stores
const typingStore = useTypingStore();
const dialogMessagesStore = useDialogMessagesStore();

const store = useStore();
const accountId = store.getters['auth/accountId'];
const { data: dialogsData, isLoading: dialogsLoading, refetch } = getAccountDialogs(accountId);
const updateDialog = updateAiDialog(accountId)

const dialogs = computed(() => dialogsData.value || []);

const dialog = computed(() => {
  if (!props.dialogId) {
    return null;
  }
  if (dialogsLoading.value) {
    return null;
  }
  if (!dialogs.value || dialogs.value.length === 0) {
    return null;
  }
  return dialogs.value.find(d => d.id === parseInt(props.dialogId)) || null;
});

watch(dialog, (newDialog) => {
  if (newDialog?.title) {
    dialogTitle.value = newDialog.title;
  }
}, { immediate: true, deep: true });

// Get user avatar
const userAvatar = computed(() => {
  return store.getters['auth/user'].avatar;
});

// Computed property to check if partner is typing in current dialog
const isScoutTyping = computed(() => {
  return typingStore.isTyping(props.dialogId);
});

// Computed property to check if last message from user
const isLastMessageFromUser = computed(() => {
  const lastVisibleMessage = visibleMessages.value[visibleMessages.value.length - 1];
  return lastVisibleMessage && lastVisibleMessage.role === 'user';
});

const storeMessages = computed(() => {
  return dialogMessagesStore.getDialogMessages(props.dialogId);
});

// Format date using date-fns
function formatDate(timestamp) {
  if (!timestamp) return '';

  try {
    // Convert UNIX timestamp to Date object using date-fns
    const date = fromUnixTime(timestamp);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return '';
    }

    // Check if today using date-fns
    if (isToday(date)) {
      return 'Today';
    }

    // Check if yesterday using date-fns
    if (isYesterday(date)) {
      return 'Yesterday';
    }

    // For other dates, format using date-fns with US locale
    return format(date, 'MMMM d, yyyy', { locale: enUS });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
}

// Determine if date divider should be shown
// Show only for the first message of each day
let lastShownDateKey = null;

function showDateDivider(dateKey) {
  if (lastShownDateKey === null || lastShownDateKey !== dateKey) {
    lastShownDateKey = dateKey;
    return true;
  }
  return false;
}

// Group messages by date using date-fns
const groupedMessages = computed(() => {
  if (!visibleMessages.value || visibleMessages.value.length === 0) return {};

  // Reset last shown date tracking when messages change
  lastShownDateKey = null;

  const groups = {};
  visibleMessages.value.forEach(message => {
    // Get date from timestamp without hours/minutes (day only) using date-fns
    const messageDate = fromUnixTime(message.created_at);
    const dayStart = startOfDay(messageDate);
    const dateKey = getUnixTime(dayStart); // Get UNIX timestamp for start of day

    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }

    groups[dateKey].push(message);
  });

  // Sort groups by date (old to new)
  const sortedGroups = {};
  Object.keys(groups)
    .sort((a, b) => {
      return parseInt(a) - parseInt(b);
    })
    .forEach(key => {
      sortedGroups[key] = groups[key];
    });

  return sortedGroups;
});

// Function to disconnect from Echo channel
const disconnectFromEchoChannel = (dialogId) => {
  if (!window.Echo || !dialogId) return;

  try {
    Echo.leave(`dialogs.${dialogId}.messages`);
    console.log(`Left channel: dialogs.${dialogId}.messages`);
  } catch (error) {
    console.error('Error leaving channel:', error);
  }
};

// Function to connect to Echo channel
const connectToEchoChannel = async (dialogId) => {
  if (!dialogId) return;

  if (!window.Echo) {
    console.warn("NO ECHO FOUND");
    return;
  }

  // Add small delay to avoid race conditions
  await new Promise(resolve => setTimeout(resolve, 100));

  try {
    const channel = Echo.private(`dialogs.${dialogId}.messages`);

    channel.listen('AiMessageCreated', (event) => {
      console.log(`Received message on dialogs.${event.dialog_id}.messages:`, event.message);
      dialogMessagesStore.addMessage(event.dialog_id, event.message);

      if (event.message.stop_reason === 'stop') {
        typingStore.stopTyping(event.dialog_id);
      } else {
        typingStore.startTyping(event.dialog_id);
      }
    });

    // Handle connection errors
    channel.error((error) => {
      // Attempt to reconnect after 2 seconds
      setTimeout(() => {
        if (props.dialogId === dialogId && window.Echo) {
          connectToEchoChannel(dialogId); // Recursive call for reconnection
        }
      }, 2000);
    });
  } catch (error) {
    console.error(`Failed to connect to channel dialogs.${dialogId}.messages:`, error);
  }
};

watch(() => props.dialogId, async (newDialogId, oldDialogId) => {
  if (!newDialogId) return;

  liveMessages.value = [];
  isLoading.value = true;

  // Disconnect from old channel if it exists
  if (oldDialogId) {
    disconnectFromEchoChannel(oldDialogId);
  }

  const { data, isLoading: messagesLoading } = getAiDialogMessages(newDialogId);

  watch(data, (newMessages) => {
    if (newMessages && newMessages.length > 0) {
      dialogMessagesStore.setDialogMessages(newDialogId, newMessages);

      const lastMessage = newMessages[newMessages.length - 1];
      if (lastMessage) {
        if (lastMessage.stop_reason === 'stop') {
          typingStore.stopTyping(newDialogId);
        } else {
          typingStore.startTyping(newDialogId);
        }
      }
    }
  }, { immediate: true });

  watch(messagesLoading, (loading) => {
    isLoading.value = loading;
  }, { immediate: true });

  // Connect to new channel
  await connectToEchoChannel(newDialogId);
}, {
  immediate: true,
  flush: 'post',
});


onUnmounted(() => {
  // Disconnect from channel on component unmount
  if (props.dialogId) {
    disconnectFromEchoChannel(props.dialogId);
  }
});

const allMessages = computed(() => {
  return storeMessages.value;
});

const visibleMessages = computed(() => {
  if (!allMessages.value || allMessages.value.length === 0) return [];
  return allMessages.value;
});

const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
};

function updateTitle(newTitle) {
  event.trigger(event.names.SCOUT_CHAT_TITLE_UPDATED);

  updateDialog.mutate({id: props.dialogId, title: newTitle}, { onSuccess: (dialog) => {
      dialogTitle.value = newTitle;
      refetch()
  }})
}

const handleFeedbackUpdated = (updatedMessage) => {
  dialogMessagesStore.updateMessage(props.dialogId, updatedMessage);
};

// Watch for changes in messages and scroll to bottom
watch(visibleMessages, () => {
  scrollToBottom();
}, { deep: true,  immediate: true});

// Process messages to group AI responses following a user question
function processMessagesIntoGroups(messages) {
  if (!messages || messages.length === 0) return [];

  const groups = [];
  let currentUserMessage = null;
  let currentThinkingGroup = null;
  let currentActionGroup = null;

  // Create a map to store tool results by tool_call_id
  const toolResultsMap = new Map();

  // First pass: collect all tool results
  messages.forEach(message => {
    if (message.role === 'tool' && message.content) {
      message.content.forEach(block => {
        if (block.type === 'tool_result' && block.tool_call_id) {
          try {
            const result = JSON.parse(block.content);
            toolResultsMap.set(block.tool_call_id, result);
          } catch (e) {
            console.warn('Failed to parse tool result:', block.content);
          }
        }
      });
    }
  });

  for (let i = 0; i < messages.length; i++) {
    const message = messages[i];

    if (message.role === 'user') {
      // If we have a thinking group in progress, add it to groups
      if (currentThinkingGroup && currentThinkingGroup.messages.length > 0) {
        groups.push(currentThinkingGroup);
        currentThinkingGroup = null;
      }
      if (currentActionGroup && currentActionGroup.messages.length > 0) {
        groups.push(currentActionGroup);
        currentActionGroup = null;
      }

      // Create a new user message group
      currentUserMessage = { ...message, role: 'user' };
      groups.push(currentUserMessage);

      // Start a new thinking group
      currentThinkingGroup = {
        role: 'thinking',
        messages: [],
      };
    } else {
      const isFinalAnswer = message.stop_reason === 'stop';
      const uiActions = message.content.filter((block) => {
        return block.type === 'tool_call' && /^ui_.*/.test(block.name);
      });
      const textBlocks = message.content.filter(
        (block) => block.type === 'text',
      );

      if (uiActions.length > 0) {
        if (!currentActionGroup) {
          currentActionGroup = {
            role: 'action',
            messages: [],
          };
        }
        // Enhance actions with tool results
        uiActions.forEach((action) => {
          const enhancedAction = { ...action, account_id: message.account_id };
          const toolResult = toolResultsMap.get(action.id);
          if (toolResult) {
            enhancedAction.toolResult = toolResult;
          }
          currentActionGroup.messages.push(enhancedAction);
        });
      }

      if (isFinalAnswer) {
        if (currentThinkingGroup && currentThinkingGroup.messages.length > 0) {
          groups.push(currentThinkingGroup);
          currentThinkingGroup = null;
        }
        groups.push({ ...message, role: 'answer' });
        if (currentActionGroup && currentActionGroup.messages.length > 0) {
          groups.push(currentActionGroup);
          currentActionGroup = null;
        }
      } else {
        if (textBlocks.length > 0) {
          if (!currentThinkingGroup) {
            currentThinkingGroup = {
              role: 'thinking',
              messages: [],
            };
          }
          currentThinkingGroup.messages.push(message);
        }
      }
    }
  }

  // Add the last thinking group if it exists
  if (currentThinkingGroup && currentThinkingGroup.messages.length > 0) {
    groups.push(currentThinkingGroup);
  }

  return groups;
}

onMounted(() => {
  setTimeout(scrollToBottom, 300);
})
</script>
